import styles from './enter-search-hint.module.css'

import React, { useEffect, useState } from 'react'

import { Flex, Text } from 'components/ds'

export const HINT_DELAY_MS = 1500
export const LEARNED_AFTER_ENTER_SEARCHES = 5
const STORAGE_KEY = 'search_enter_hint_enter_count'

const readEnterSearchCount = (): number => {
  try {
    return Number(window.localStorage.getItem(STORAGE_KEY)) || 0
  } catch {
    return 0
  }
}

const writeEnterSearchCount = (count: number): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(count))
  } catch {
    // Storage blocked: the hint keeps showing, which is harmless.
  }
}

/** Counts an Enter search towards retiring the hint for this browser. */
export const recordEnterSearch = (): void => {
  const count = readEnterSearchCount()
  if (count < LEARNED_AFTER_ENTER_SEARCHES) writeEnterSearchCount(count + 1)
}

type Props = {
  /** The input holds text that hasn't been searched yet. */
  pending: boolean
  /** The input's text; each change restarts the delay. */
  searchTerm: string
}

/**
 * Tooltip under the search input telling users to press Enter once they stop
 * typing, as search no longer runs on a pause. Retires itself after a few
 * Enter searches, or at once when dismissed.
 */
const EnterSearchHint = ({ pending, searchTerm }: Props): React.ReactNode => {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    setVisible(false)
    if (!pending || readEnterSearchCount() >= LEARNED_AFTER_ENTER_SEARCHES) {
      return
    }
    const timer = setTimeout(() => setVisible(true), HINT_DELAY_MS)
    return () => clearTimeout(timer)
  }, [pending, searchTerm])

  const dismiss = () => {
    writeEnterSearchCount(LEARNED_AFTER_ENTER_SEARCHES)
    setVisible(false)
  }

  if (!visible) return null
  return (
    <Flex align="center" gap="2" className={styles.tooltip} role="status">
      <Text size="1">Press ↵ Enter to search</Text>
      <button
        type="button"
        className={styles.dismiss}
        aria-label="Dismiss Enter hint"
        // Out of the Tab order: leaving the input hides the hint, so Tab could
        // never land here, and it shouldn't hold up Tab moving on either.
        tabIndex={-1}
        // Keeps focus in the input; a blur would hide the hint before the click lands.
        onMouseDown={e => e.preventDefault()}
        onClick={dismiss}
      >
        ×
      </button>
    </Flex>
  )
}

export default EnterSearchHint
